/** /zh/use-cases/analyst-investment-memo — Chinese version. */
import { MarketingShell } from '../MarketingShell';

export function AnalystInvestmentMemoZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/use-cases/analyst-investment-memo"
      documentTitle="使用场景：分析师投资备忘录 · Draftlize"
      documentDescription="分析师和投资人怎么在 Draftlize 里建投资备忘录。source → fact → claim → decision 链，每个 claim 引底层观察，决策在假设动了时自动 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">使用场景 · 投资备忘录</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            投资备忘录<br/>
            <em>看得见过程。</em>
          </h1>
          <p className="lede">
            给建投资 case 的分析师。source → fact → claim → decision 链，每个 claim 引底层观察，决策在假设动了时自动 stale。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">标准分析师工作流：<em>漏在哪。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">来源埋在文档里</h3>
            <p>你在 memo 里引一个 10-K 数字。3 周后合伙人问"来源是什么？"。你搜 47 份 PDF。可能找到，可能没。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Claim 跟 fact 漂移</h3>
            <p>你第 1 周基于 Q3 分部数据写"TAM $4B"。Q4 数据修正 TAM 到 $3.1B。你 memo 还是 $4B。没人发现。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">有信念没溯源</h3>
            <p>你写"BUY PT $85"。是什么推理链到这的？标准 memo 是散文；3 周后你重构不出价格目标基于哪些观察。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Draftlize <em>分析师循环。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">来源即观察</h3>
            <p>把 10-K、电话会议 transcript、行业报告丢进 <code>_refs/</code>。agent 读，对每个引用数字建 <code>observation</code> 卡，带 <code>→derived-from [[10-K-q3-2025.pdf]]</code> wikilink。每个数字都能溯源。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Fact 和 claim 都是卡片</h3>
            <p><code>c-04 fact: 2025 Q3 毛利率 38%</code> 引 3 个观察。<code>c-08 claim: FY27 毛利率扩到 45%</code>，带 <code>→supports c-04, c-12, c-15</code>。Claim 卡跟 fact 并列但认识论状态明显不同。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">决策有显式链</h3>
            <p><code>c-20 decision: BUY PT $85</code>，带 <code>→derived-from</code> 一棵 claim 树，每个由 fact 支撑，每个引观察。链可查询。改一个 fact → claim 标 stale → 决策标 stale。合伙人审计轨迹自动有。</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">真实工作流：<em>跟踪一家组合公司 6 个月。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">M1</span>
            <h3 className="serif">初始 memo</h3>
            <p>~60 卡片跨 observation / fact / claim / decision。原始 BUY PT 卡下面有 4 层溯源链。合伙人周五签字。</p>
          </div>
          <div className="problem-col">
            <span className="num">M3</span>
            <h3 className="serif">Q4 财报出来</h3>
            <p>你更新 3 张观察卡（收入、毛利率、流失）。stale 传播标记 12 张链上的依赖卡——包括 BUY PT 决策。你看到哪些假设动了，有意识地重新推导结论。</p>
          </div>
          <div className="problem-col">
            <span className="num">M6</span>
            <h3 className="serif">跨组合冲突</h3>
            <p>你 memo 说"竞争护城河来自网络效应"。同事一份关于竞品的 memo 说"这个品类没有真护城河"。跨 project 冲突检测（启用后）在两边都标 gap 卡。合伙人看到两边，决定哪个对。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          substrate 强制你把链显式化。每个 claim 都有引用的 fact。每个 decision 都有可溯源的依据。合伙人 review 从"相信我"变"点开查"。
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">在<em>一个 memo</em>上试。</h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            注册送的 $5 够种一个完整 memo（~40-80 卡）并感受一次财报更新重新触发链。多数分析师第 3 天就发现审计轨迹的价值。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
